/**
 * App Component
 *
 * The root component that sets up:
 * - Routing with React Router
 * - Main application structure
 * - Error boundaries for better error handling
 * - Code splitting for better performance
 * - Theme management (dark/light mode)
 */
import React, { Suspense, lazy, useEffect, useState, useMemo } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";

import AppInitializer from "./components/common/AppInitializer";
import ErrorBoundary from "./components/common/ErrorBoundary";
import ScrollToTop from "./components/common/ScrollToTop";
import ContactModalProvider from "./components/contact/ContactModalProvider";
import { CookieComponents } from "./components/ui/CookieComponents";
import LoadingFallback from "./components/ui/LoadingFallback";
import ScrollProgress from "./components/ui/ScrollProgress";
import WebGLWarningBanner from "./components/ui/WebGLWarningBanner";
import AnimationSettingsProvider from "./context/AnimationSettingsProvider";
import BackgroundProvider from "./context/BackgroundProvider";
import { CookieConsentProvider } from "./context/CookieConsentContext";
import { ScrollDirectionProvider } from "./context/ScrollDirectionProvider";
import { ServiceDetailsProvider } from "./context/ServiceDetailsProvider";
import ThemeProvider from "./context/ThemeProvider";
import { logPageView } from "./hooks/useAnalytics";
import { useBackground } from "./hooks/useBackground";
import { setupResponsiveListener } from "./utils/browserDetection";
import { useProfiler, ProfilingDisplay } from "./utils/reactProfiler";
import { isChromeUsingOpenGL } from "./utils/webglDetection";
import "./App.css";

// Lazy load the background component
const AnimatedBackground = lazy(() => import("./components/background/AnimatedBackground"));

// Lazy load layout and pages for code splitting
const Layout = lazy(() => import("./components/layout/Layout"));

// Lazy load all pages
const HomePage = lazy(() => import("./pages/HomePage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

// Lazy-load AnimatePresence from framer-motion to defer loading animations
const LazyAnimatePresence = lazy(() => import('framer-motion').then(mod => ({ default: mod.AnimatePresence })));

/**
 * App component
 *
 * Main application component that sets up routing and global providers
 */
const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ThemeProvider>
          <AnimationSettingsProvider>
            <BackgroundProvider>
              <ScrollDirectionProvider>
                <ContactModalProvider>
                  <ServiceDetailsProvider>
                    <CookieConsentProvider>
                      <AppInitializer>
                        <InnerApp />
                        <CookieComponents />
                      </AppInitializer>
                    </CookieConsentProvider>
                  </ServiceDetailsProvider>
                </ContactModalProvider>
              </ScrollDirectionProvider>
            </BackgroundProvider>
          </AnimationSettingsProvider>
        </ThemeProvider>
      </Router>
    </ErrorBoundary>
  );
};

/**
 * InnerApp component that uses the providers' context
 */
const InnerApp: React.FC = () => {
  // Log page views on route change
  const location = useLocation();
  useEffect(() => {
    // Handle the Promise returned by logPageView
    logPageView(location.pathname).catch(error => {
      console.error('Failed to log page view:', error);
    });
  }, [location.pathname]);

  // Get background state from context
  const { variant, showContent } = useBackground();

  // Add profiler hook here
  const { showProfiler } = useProfiler();
  
  // Track whether to show the WebGL warning
  const [showWebGLWarning, setShowWebGLWarning] = useState(false);
  
  // Force re-render on resize for responsive design
  const [, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  
  // Setup responsive detection and handling
  useEffect(() => {
    // Set up responsive listener to update state when viewport size changes
    const listener = setupResponsiveListener((_isMobile) => {
      // Force re-render to apply mobile/desktop styles correctly
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    });
    
    // Clean up listener on unmount
    return () => listener.remove();
  }, []);
  
  // Check for problematic WebGL configuration on mount
  useEffect(() => {
    // Short delay to let the application initialize
    const checkTimeout = setTimeout(() => {
      const hasOpenGLIssue = isChromeUsingOpenGL();
      setShowWebGLWarning(hasOpenGLIssue);
    }, 1000);
    
    return () => clearTimeout(checkTimeout);
  }, []);
  
  // Remove preload background element once the component mounts
  useEffect(() => {
    const preloadEl = document.getElementById('bg-preload');
    if (preloadEl && preloadEl.parentNode) {
      preloadEl.parentNode.removeChild(preloadEl);
    }
  }, []);

  return (
    <div className="App" style={{ 
      position: 'relative', 
      minHeight: '100vh',
      background: 'transparent' // Ensure no solid background is applied
    }}>
      {/* Chrome OpenGL detection warning banner */}
      {showWebGLWarning && <WebGLWarningBanner />}
      
      {/* Add the ProfilingDisplay component (only in development) */}
      {process.env.NODE_ENV === 'development' && <ProfilingDisplay show={showProfiler} />}
      
      {/* Global animated background for the entire application - Lazy loaded */}
      <Suspense fallback={null}>
        <BackgroundRenderer 
          variant={variant} 
          intensity="medium"
        />
      </Suspense>
      
      <div style={{ 
          position: 'relative', 
          zIndex: 1, 
          opacity: showContent ? 1 : 0, 
          transition: 'opacity 0.5s ease-in-out' 
      }}>
        {/* Adding modern UI elements */}
        <ScrollProgress />
        <ScrollToTop />

        {/* Defer loading of framer-motion until routes render */}
        <Suspense fallback={null}>
          <LazyAnimatePresence mode="wait">
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/" element={<Layout />}>
                  <Route index element={<HomePage />} />
                  <Route path="services" element={<ServicesPage />} />
                  <Route path="privacy" element={<PrivacyPage />} />
                  <Route path="terms" element={<TermsPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Routes>
            </Suspense>
          </LazyAnimatePresence>
        </Suspense>
      </div>
    </div>
  );
};

/**
 * Renders the appropriate background with page-specific optimizations
 */
const BackgroundRenderer: React.FC<{
  variant: 'default' | 'services';
  intensity: 'low' | 'medium' | 'high';
}> = ({ variant, intensity }) => {
  const location = useLocation();
  
  // Memoize page checks to prevent unnecessary recalculations
  const { isHomePage, isServicesPage } = useMemo(() => ({
    isHomePage: location.pathname === '/',
    isServicesPage: location.pathname.includes('/services')
  }), [location.pathname]);
  
  // Defer background loading on Home page for performance
  if (isHomePage) {
    return (
      <AnimatedBackground
        variant={variant}
        intensity={intensity}
        priority="low"
      />
    );
  }
  
  // Different priority levels based on page type
  if (isServicesPage) {
    return (
      <AnimatedBackground 
        variant={variant} 
        intensity={intensity}
        priority="high" // Changed from "low" to "high" to match homepage loading speed
      />
    );
  }
  
  // Default configuration for other pages
  return (
    <AnimatedBackground 
      variant={variant} 
      intensity={intensity}
      priority="high"
    />
  );
};

export default App;
